import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDesktopDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 72;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAnchorClick = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    scrollToSection(id);
  };

  const handleMobileAnchorClick = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileOpen(false);
    setTimeout(() => scrollToSection(id), 200);
  };

  const textClass = scrolled ? 'text-foreground-700' : 'text-background-50/90';
  const textHoverClass = scrolled ? 'hover:text-foreground-950' : 'hover:text-background-50';
  const logoClass = scrolled ? 'text-foreground-950' : 'text-background-50';
  const borderClass = scrolled ? 'border-background-200/60' : 'border-background-50/15';
  const bgClass = scrolled ? 'bg-background-50/95 backdrop-blur-md' : 'bg-transparent';
  const dropdownBg = scrolled ? 'bg-background-50 border-background-200/80' : 'bg-foreground-900/90 backdrop-blur-md border-background-50/10';
  const dropdownText = scrolled ? 'text-foreground-700' : 'text-background-50/80';
  const dropdownHoverBg = scrolled ? 'hover:bg-background-100' : 'hover:bg-background-50/10';
  const dropdownHoverText = scrolled ? 'hover:text-foreground-950' : 'hover:text-background-50';

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${bgClass} ${
        scrolled ? 'border-b' : ''
      } ${borderClass}`}
    >
      <div className="flex items-center justify-between h-16 px-6 md:px-10">
        <Link
          to="/"
          onClick={handleHomeClick}
          className={`font-heading text-xl md:text-2xl font-semibold tracking-tight whitespace-nowrap transition-colors ${logoClass}`}
        >
          itaobuy<span className={scrolled ? 'text-primary-500' : 'text-primary-300'}>.</span>help
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <button
            onClick={handleHomeClick}
            className={`text-sm transition-colors whitespace-nowrap cursor-pointer bg-transparent border-0 ${textClass} ${textHoverClass}`}
          >
            <i className="ri-home-4-line mr-1"></i> Home
          </button>
          <button
            onClick={handleAnchorClick('guides')}
            className={`text-sm transition-colors whitespace-nowrap cursor-pointer bg-transparent border-0 ${textClass} ${textHoverClass}`}
          >
            <i className="ri-book-open-line mr-1"></i> Shopping Guide
          </button>
          <button
            onClick={handleAnchorClick('what-is')}
            className={`text-sm transition-colors whitespace-nowrap cursor-pointer bg-transparent border-0 ${textClass} ${textHoverClass}`}
          >
            Guides
          </button>
          <button
            onClick={handleAnchorClick('comparison')}
            className={`text-sm transition-colors whitespace-nowrap cursor-pointer bg-transparent border-0 ${textClass} ${textHoverClass}`}
          >
            Compare
          </button>
          <button
            onClick={handleAnchorClick('safety')}
            className={`text-sm transition-colors whitespace-nowrap cursor-pointer bg-transparent border-0 ${textClass} ${textHoverClass}`}
          >
            Safety
          </button>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDesktopDropdownOpen(!desktopDropdownOpen)}
              className={`flex items-center gap-1.5 text-sm transition-colors whitespace-nowrap cursor-pointer bg-transparent border-0 ${textClass} ${textHoverClass}`}
            >
              Categories
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${desktopDropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {desktopDropdownOpen && (
              <div className={`absolute top-full mt-2 right-0 w-56 rounded-xl py-2 overflow-hidden shadow-lg ${dropdownBg}`}>
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className={`flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors whitespace-nowrap ${dropdownText} ${dropdownHoverBg} ${dropdownHoverText}`}
                  >
                    <i className={`${cat.icon} text-xs opacity-60`}></i>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`hidden md:inline-flex items-center px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
              scrolled
                ? 'text-primary-500 border border-primary-300 hover:bg-primary-50'
                : 'text-background-50 border border-background-50/40 hover:bg-background-50/10'
            }`}
          >
            itaobuy sheet
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center px-5 py-2 text-sm font-semibold text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Buy It Now
          </a>

          <button
            className="md:hidden flex items-center justify-center w-9 h-9 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl ${scrolled ? 'text-foreground-950' : 'text-background-50'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className={`md:hidden border-t px-4 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto ${scrolled ? 'bg-background-50 border-background-200/60' : 'bg-foreground-900/95 border-background-50/10'}`}>
          <div className="flex items-center gap-2 mb-3 pb-3 border-b border-background-50/10">
            <button
              className={`py-2 px-3 text-sm rounded-lg flex items-center gap-2 whitespace-nowrap transition-colors bg-transparent border-0 cursor-pointer ${scrolled ? 'text-foreground-700 hover:bg-background-100' : 'text-background-50/80 hover:bg-background-50/10'}`}
              onClick={() => {
                setMobileOpen(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <i className="ri-home-4-line text-base"></i> Home
            </button>
            <button
              className={`py-2 px-3 text-sm rounded-lg flex items-center gap-2 whitespace-nowrap transition-colors bg-transparent border-0 cursor-pointer ${scrolled ? 'text-foreground-700 hover:bg-background-100' : 'text-background-50/80 hover:bg-background-50/10'}`}
              onClick={handleMobileAnchorClick('guides')}
            >
              <i className="ri-book-open-line text-base"></i> Shopping Guide
            </button>
          </div>

          <div className="flex flex-wrap gap-2 mb-3">
            <button onClick={handleMobileAnchorClick('what-is')} className={`py-1.5 px-3 text-xs rounded-full border transition-colors whitespace-nowrap bg-transparent cursor-pointer ${scrolled ? 'text-foreground-600 border-background-300 hover:bg-background-100' : 'text-background-50/70 border-background-50/20 hover:bg-background-50/10'}`}>
              <i className="ri-guide-line mr-1"></i>Guides
            </button>
            <button onClick={handleMobileAnchorClick('comparison')} className={`py-1.5 px-3 text-xs rounded-full border transition-colors whitespace-nowrap bg-transparent cursor-pointer ${scrolled ? 'text-foreground-600 border-background-300 hover:bg-background-100' : 'text-background-50/70 border-background-50/20 hover:bg-background-50/10'}`}>
              <i className="ri-scales-3-line mr-1"></i>Compare
            </button>
            <button onClick={handleMobileAnchorClick('safety')} className={`py-1.5 px-3 text-xs rounded-full border transition-colors whitespace-nowrap bg-transparent cursor-pointer ${scrolled ? 'text-foreground-600 border-background-300 hover:bg-background-100' : 'text-background-50/70 border-background-50/20 hover:bg-background-50/10'}`}>
              <i className="ri-shield-check-line mr-1"></i>Safety
            </button>
          </div>

          <p className={`pt-1 pb-2 text-[10px] font-semibold uppercase tracking-widest ${scrolled ? 'text-foreground-400' : 'text-background-50/35'}`}>Categories</p>
          <div className="grid grid-cols-2 gap-1.5">
            {categories.map((cat) => (
              <a
                key={cat.seq}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className={`flex items-center gap-2 py-2 px-2.5 rounded-lg text-sm transition-colors ${scrolled ? 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950' : 'text-background-50/70 hover:bg-background-50/10 hover:text-background-50'}`}
                onClick={() => setMobileOpen(false)}
              >
                <span className={`w-6 h-6 flex items-center justify-center rounded-md shrink-0 ${scrolled ? 'bg-background-200/60 text-foreground-500' : 'bg-background-50/15 text-background-50/60'}`}>
                  <i className={`${cat.icon} text-xs`}></i>
                </span>
                <span className="truncate text-xs">{cat.name}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}